import React from 'react';
import { Loader2, Save, ClipboardList, MailCheck } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Field, Select } from '../ui/Field';
import { pillPrimary } from '../ui/pills';
import { useDossierContext } from './SuiviPrivatisationLayout';
import { BriefQuestionsForm, BriefQuestion } from './BriefQuestionsForm';
import { ClientDemarcheAnswers } from './ClientDemarcheAnswers';

/**
 * Onglet « Brief » — les informations détaillées récoltées auprès du client avant de chiffrer.
 *
 * AUCUNE QUESTION N'EST CODÉE ICI. Elles viennent d'un modèle de Démarche coché « Utiliser ces questions
 * comme brief » dans Paramètres > Démarches. C'est délibéré : « je ne sais pas quelles questions elles
 * posent » (Emeni, 2026-08-26) — ce sont ses commerciales qui les connaissent, et elles doivent pouvoir les
 * écrire elles-mêmes sans passer par un développeur.
 *
 * Depuis le 2026-09-01 (demande d'Emeni), plusieurs modèles peuvent porter cette case, et CE dossier choisit
 * le sien dans la liste ci-dessous — ce n'est plus forcément la même Démarche que celle envoyée au client.
 * Ses réponses à elle restent affichées à part, toujours, quel que soit le modèle de Brief choisi.
 *
 * Les réponses du Brief sont enregistrées au format exact des Démarches, pour qu'un brief rempli ici par la
 * commerciale et un brief rempli par le client lui-même produisent la même donnée.
 */
export function DossierBriefPage() {
    const { dossier } = useDossierContext();
    const [chargement, setChargement] = React.useState(true);
    const [modele, setModele] = React.useState<{ id: string; name: string; questions: BriefQuestion[] } | null>(null);
    const [disponibles, setDisponibles] = React.useState<{ id: string; name: string }[]>([]);
    const [reponses, setReponses] = React.useState<Record<string, string>>({});
    const [maj, setMaj] = React.useState<{ at: string | null; par: string }>({ at: null, par: '' });
    const [enregistrement, setEnregistrement] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [enregistre, setEnregistre] = React.useState(false);
    const [changementModele, setChangementModele] = React.useState(false);
    const [clientDemarcheNom, setClientDemarcheNom] = React.useState('');
    const [clientReponses, setClientReponses] = React.useState<{ id: string; question: string; answer: string }[]>([]);
    // Combien de réponses viennent d'être reprises du questionnaire du client plutôt que saisies à la main
    // (cf. GET .../brief côté serveur) — à vérifier avant d'enregistrer, pas à faire confiance les yeux fermés.
    const [nbPreremplies, setNbPreremplies] = React.useState(0);

    const charger = React.useCallback(() => {
        return moduleApi.getDossierBrief(dossier.dossier_id)
            .then((r: any) => {
                setModele(r?.template || null);
                setDisponibles(Array.isArray(r?.available_templates) ? r.available_templates : []);
                const map: Record<string, string> = {};
                (r?.answers || []).forEach((a: any) => { map[a.id] = a.answer || ''; });
                setReponses(map);
                setMaj({ at: r?.updated_at || null, par: r?.updated_by_label || '' });
                setNbPreremplies(Number(r?.prefilled_count) || 0);
                setClientDemarcheNom(r?.client_demarche_name || '');
                setClientReponses(Array.isArray(r?.client_answers) ? r.client_answers : []);
            })
            .catch((e: any) => setErreur(e?.message || 'Chargement impossible.'));
    }, [dossier.dossier_id]);

    React.useEffect(() => { charger().finally(() => setChargement(false)); }, [charger]);

    const changerModele = async (templateId: string) => {
        setChangementModele(true);
        setErreur('');
        try {
            await moduleApi.setDossierBriefTemplate(dossier.dossier_id, templateId);
            await charger();
            setEnregistre(false);
        } catch (e: any) {
            setErreur(e?.message || "Le changement de modèle n'a pas pu être enregistré.");
        } finally {
            setChangementModele(false);
        }
    };

    const modifier = (id: string, valeur: string) => {
        setReponses((prev) => ({ ...prev, [id]: valeur }));
        setEnregistre(false);
    };

    const enregistrer = async () => {
        if (!modele) return;
        setEnregistrement(true);
        setErreur('');
        try {
            // On renvoie TOUTES les questions du modèle, y compris celles restées vides : sinon une réponse
            // effacée resterait celle d'avant, puisque l'enregistrement remplace l'ensemble.
            const answers = modele.questions.map((q) => ({
                id: q.id,
                question: q.question,
                type: q.type,
                answer: reponses[q.id] || '',
            }));
            await moduleApi.saveDossierBrief(dossier.dossier_id, { template_id: modele.id, answers });
            setEnregistre(true);
        } catch (e: any) {
            setErreur(e?.message || "Le brief n'a pas pu être enregistré.");
        } finally {
            setEnregistrement(false);
        }
    };

    if (chargement) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-content-muted" size={22} /></div>;

    return (
        <div className="space-y-4">
            <ClientDemarcheAnswers demarcheName={clientDemarcheNom} answers={clientReponses} />

            {disponibles.length > 0 && (
                <Field label="Questionnaire de brief pour ce dossier">
                    {({ id }) => (
                        <Select id={id} value={modele?.id || ''} onChange={(e) => changerModele(e.target.value)} disabled={changementModele}>
                            <option value="">— Aucun choisi —</option>
                            {disponibles.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </Select>
                    )}
                </Field>
            )}

            {!modele ? (
                <div className="rounded-xl border border-dashed border-border p-5 text-center space-y-2">
                    <ClipboardList size={22} className="mx-auto text-content-muted" />
                    <p className="text-sm font-bold text-content">
                        {disponibles.length > 0 ? 'Choisissez un questionnaire de brief ci-dessus' : "Aucun questionnaire de brief n'est encore défini"}
                    </p>
                    <p className="text-[11px] text-content-muted max-w-md mx-auto leading-relaxed">
                        Les questions du brief sont les vôtres : celles que vous posez déjà au client pour établir un
                        devis. Créez-les depuis le bouton « Paramètres » de l'écran Suivi privatisation
                        (« Retour au suivi » ci-dessus).
                    </p>
                </div>
            ) : (
                <>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <p className="text-[11px] text-content-muted">
                            Questionnaire : <span className="font-bold text-content">{modele.name}</span>
                        </p>
                        {maj.at && (
                            <p className="text-[10px] text-content-muted">
                                Dernière mise à jour {new Date(maj.at).toLocaleDateString('fr-FR')}{maj.par ? ` par ${maj.par}` : ''}
                            </p>
                        )}
                    </div>

                    {/* Un pré-remplissage ne joue que si ce modèle de Brief partage des identifiants de question
                        avec la Démarche du client (typiquement le même modèle utilisé pour les deux). */}
                    {nbPreremplies > 0 && (
                        <p className="text-[11px] text-content-muted flex items-center gap-1.5 p-2 rounded-lg bg-surface-soft border border-border">
                            <MailCheck size={12} className="shrink-0" />
                            {nbPreremplies === 1
                                ? 'Une réponse a été reprise du questionnaire du client — vérifiez-la avant d’enregistrer.'
                                : `${nbPreremplies} réponses ont été reprises du questionnaire du client — vérifiez-les avant d'enregistrer.`}
                        </p>
                    )}

                    <BriefQuestionsForm questions={modele.questions} valeurs={reponses} onChange={modifier} />

                    <div className="flex items-center gap-3">
                        <button type="button" className={pillPrimary(34)} onClick={enregistrer} disabled={enregistrement}>
                            {enregistrement ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Enregistrer le brief
                        </button>
                        {enregistre && <span className="text-[12px] font-semibold text-state-ok">Enregistré.</span>}
                    </div>
                </>
            )}

            {erreur && <p className="text-[12px] text-state-danger font-semibold">{erreur}</p>}
        </div>
    );
}
